import React, { useEffect, useRef, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import { Panel, Badge } from "./UIKit";
import { Activity, Heart, Wind, Circle, WifiOff } from "lucide-react";

/**
 * LiveMonitor — visualizza in real-time gli ultimi 60s di raw samples
 * dell'utente se ha una raw_session con status=active. Polling ogni 2s.
 */
export default function LiveMonitor({ userId }) {
  const { api } = useAdminAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const d = await api(`/users/${userId}/live`);
        if (!cancelled) {
          setData(d);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Errore");
      }
    };
    fetchLive();
    timerRef.current = setInterval(fetchLive, 2000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [userId, api]);

  if (err) {
    return (
      <Panel title="Live Session Monitor">
        <div className="p-4 text-rose-300 text-sm">Errore: {err}</div>
      </Panel>
    );
  }
  if (!data) {
    return (
      <Panel title="Live Session Monitor">
        <div className="p-4 text-white/50 text-sm">Connessione…</div>
      </Panel>
    );
  }

  if (!data.active) {
    return (
      <Panel
        title="Live Session Monitor"
        right={<Badge color="slate">Nessuna sessione attiva</Badge>}
      >
        <div className="p-6 flex items-center gap-3 text-white/55 text-[13px]">
          <WifiOff size={16} />
          Nessuna sessione raw attiva per questo utente. Il monitor si attiva
          automaticamente quando il sensore inizia a trasmettere.
        </div>
      </Panel>
    );
  }

  const { session, series, last, samples_count } = data;
  const age = last?.ts_ms ? Math.round((Date.now() - last.ts_ms) / 1000) : null;
  const fresh = age !== null && age < 10;

  return (
    <Panel
      title="Live Session Monitor"
      right={
        <div className="flex items-center gap-2">
          <Badge color={fresh ? "teal" : "amber"}>
            <Circle
              size={8}
              className={`mr-1.5 ${fresh ? "animate-pulse fill-current" : ""}`}
            />
            {fresh ? "LIVE" : `stale ${age}s`}
          </Badge>
          <Badge color="slate">{samples_count} samples / 60s</Badge>
        </div>
      }
    >
      <div className="p-4 space-y-4" data-testid="live-monitor">
        {/* Metric cards */}
        <div className="grid grid-cols-4 gap-2">
          <MetricTile
            icon={<Heart size={14} />}
            label="Heart rate"
            value={last?.heart_r?.toFixed(1) ?? "—"}
            unit="bpm"
            accent="rose"
          />
          <MetricTile
            icon={<Wind size={14} />}
            label="Respiration"
            value={last?.breath_r?.toFixed(1) ?? "—"}
            unit="rpm"
            accent="sky"
          />
          <MetricTile
            icon={<Activity size={14} />}
            label="On bed"
            value={last?.sleep_t === 1 ? "YES" : "NO"}
            unit=""
            accent={last?.sleep_t === 1 ? "teal" : "slate"}
          />
          <MetricTile
            icon={<Circle size={14} />}
            label="Signal"
            value={((last?.good_k ?? 0) * 100).toFixed(0)}
            unit="%"
            accent={last?.good_k > 0.5 ? "teal" : "amber"}
          />
        </div>

        {/* Live sparklines */}
        <div className="grid grid-cols-1 gap-2">
          <Sparkline
            label="Heart rate (60s)"
            values={series.heart_r}
            timestamps={series.ts_ms}
            color="#f472b6"
            minScale={40}
            maxScale={100}
            unit="bpm"
          />
          <Sparkline
            label="Respiration (60s)"
            values={series.breath_r}
            timestamps={series.ts_ms}
            color="#60a5fa"
            minScale={8}
            maxScale={24}
            unit="rpm"
          />
          <Sparkline
            label="Movement magnitude (60s)"
            values={series.movement}
            timestamps={series.ts_ms}
            color="#34d399"
            minScale={0}
            maxScale={0.3}
            unit=""
          />
        </div>

        <div className="flex items-center justify-between text-white/50 text-[11px] pt-2 border-t border-white/5">
          <span className="font-mono">session: {session.id}</span>
          <span>Started: {new Date(session.started_at).toLocaleString("it-IT")}</span>
        </div>
      </div>
    </Panel>
  );
}

function MetricTile({ icon, label, value, unit, accent = "teal" }) {
  const colors = {
    teal: "text-[#67e8d1]",
    rose: "text-rose-300",
    amber: "text-amber-300",
    sky: "text-sky-300",
    slate: "text-white/60",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={colors[accent]}>{icon}</span>
        <span className="text-white/55 text-[10px] uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-[20px] font-semibold ${colors[accent]}`}>
          {value}
        </span>
        {unit && <span className="text-white/40 text-[11px]">{unit}</span>}
      </div>
    </div>
  );
}

function Sparkline({ label, values, color, minScale, maxScale, unit }) {
  const w = 600;
  const h = 60;
  if (!values || values.length === 0) {
    return (
      <div className="text-white/40 text-[11px]">
        {label}: nessun dato
      </div>
    );
  }
  const min = Math.min(...values, minScale);
  const max = Math.max(...values, maxScale);
  const range = max - min || 1;
  const step = w / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(" ");
  const last = values[values.length - 1];
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white/55 text-[10px] uppercase tracking-wider">
          {label}
        </span>
        <span className="text-white text-[12px] font-mono">
          {last?.toFixed(2)} {unit}
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[60px]">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    </div>
  );
}
